import { Link } from 'react-router-dom'
import { motion } from 'framer-motion'
import {
  CheckCircle, MapPin, Satellite, Anchor,
  AlertTriangle, Eye, Navigation
} from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { ProductPageNav, ProductPageFooter } from '@/components/ProductPageNav'
import { SEOHead } from '@/components/SEOHead'

// ── SVG: AOI Satellite View ──────────────────────────────────────────────────

function AOISatelliteSVG() {
  const cols = 5
  const rows = 4
  const cellW = 80
  const cellH = 55
  const gapX = 4
  const gapY = 4
  const ox = 20
  const oy = 20

  // Opacity levels to suggest varying imagery density
  const opacities = [
    [0.12, 0.18, 0.25, 0.14, 0.08],
    [0.20, 0.35, 0.45, 0.30, 0.16],
    [0.14, 0.40, 0.50, 0.38, 0.20],
    [0.10, 0.18, 0.28, 0.16, 0.09],
  ]

  // AOI polygon corners (spans ~3 cols × 2 rows, starting at col 1, row 1)
  const aoi = [
    [ox + 1 * (cellW + gapX) + 10,         oy + 1 * (cellH + gapY) + 8],
    [ox + 3 * (cellW + gapX) + cellW - 10,  oy + 1 * (cellH + gapY) + 8],
    [ox + 3 * (cellW + gapX) + cellW - 10,  oy + 2 * (cellH + gapY) + cellH - 8],
    [ox + 1 * (cellW + gapX) + 10,          oy + 2 * (cellH + gapY) + cellH - 8],
  ]
  const aoiPoints = aoi.map(([x, y]) => `${x},${y}`).join(' ')

  // Change detection boxes (inside AOI)
  const changeBoxes = [
    { x: ox + 1 * (cellW + gapX) + 18, y: oy + 1 * (cellH + gapY) + 16, w: 38, h: 26 },
    { x: ox + 2 * (cellW + gapX) + 22, y: oy + 2 * (cellH + gapY) - 10, w: 44, h: 28 },
  ]

  // Timeline at bottom
  const years = ['2019', '2020', '2021', '2022', '2023']
  const tlY = oy + rows * (cellH + gapY) + 16
  const tlX1 = ox
  const tlX2 = ox + cols * (cellW + gapX) - gapX

  return (
    <svg viewBox="0 0 640 300" className="w-full" aria-label="AOI satellite view">
      {/* Map tile grid */}
      {Array.from({ length: rows }, (_, r) =>
        Array.from({ length: cols }, (_, c) => (
          <rect
            key={`${r}-${c}`}
            x={ox + c * (cellW + gapX)}
            y={oy + r * (cellH + gapY)}
            width={cellW}
            height={cellH}
            rx="3"
            fill={`rgba(34,197,94,${opacities[r][c]})`}
            stroke="#1e3a1e"
            strokeWidth="1"
          />
        ))
      )}

      {/* AOI boundary polygon */}
      <polygon
        points={aoiPoints}
        fill="rgba(245,158,11,0.06)"
        stroke="#f59e0b"
        strokeWidth="2"
        strokeDasharray="8 4"
      />
      <text
        x={aoi[0][0] + 4}
        y={aoi[0][1] - 5}
        fill="#f59e0b"
        fontSize="9"
        fontWeight="700"
      >AOI — Monitoring Active</text>

      {/* Change detection boxes */}
      {changeBoxes.map((b, i) => (
        <g key={i}>
          <rect
            x={b.x} y={b.y} width={b.w} height={b.h}
            rx="3"
            fill="rgba(239,68,68,0.12)"
            stroke="#ef4444"
            strokeWidth="1.5"
          />
          <text x={b.x + b.w / 2} y={b.y + b.h / 2 + 4}
            textAnchor="middle" fill="#ef4444" fontSize="8" fontWeight="700">
            Change +12%
          </text>
        </g>
      ))}

      {/* Satellite icon — top right */}
      <circle cx="598" cy="38" r="16" fill="#0d1117" stroke="#22c55e" strokeWidth="1.5" />
      {/* Body */}
      <rect x="591" y="34" width="14" height="9" rx="2" fill="none" stroke="#22c55e" strokeWidth="1.2" />
      {/* Solar wings */}
      <rect x="576" y="36" width="12" height="5" rx="1" fill="#22c55e" opacity="0.6" />
      <rect x="608" y="36" width="12" height="5" rx="1" fill="#22c55e" opacity="0.6" />
      <text x="598" y="62" textAnchor="middle" fill="#64748b" fontSize="8">SAT</text>

      {/* Timeline bar */}
      <line x1={tlX1} y1={tlY + 8} x2={tlX2} y2={tlY + 8}
        stroke="#1e3a1e" strokeWidth="2" />
      {years.map((yr, i) => {
        const tx = tlX1 + (i / (years.length - 1)) * (tlX2 - tlX1)
        const isLatest = i === years.length - 1
        return (
          <g key={yr}>
            {isLatest && (
              <circle cx={tx} cy={tlY + 8} r="10"
                fill="none" stroke="#22c55e" strokeWidth="1.5" opacity="0.4" />
            )}
            <circle cx={tx} cy={tlY + 8} r={isLatest ? 6 : 4}
              fill={isLatest ? '#22c55e' : '#1e3a1e'}
              stroke={isLatest ? '#22c55e' : '#374151'}
              strokeWidth="1.5"
            />
            <text x={tx} y={tlY + 26}
              textAnchor="middle"
              fill={isLatest ? '#22c55e' : '#64748b'}
              fontSize="9"
              fontWeight={isLatest ? '700' : '400'}
            >{yr}</text>
            {isLatest && (
              <text x={tx} y={tlY + 38} textAnchor="middle" fill="#22c55e" fontSize="8">Latest</text>
            )}
          </g>
        )
      })}
    </svg>
  )
}

// ── SVG: Imagery Timeline ────────────────────────────────────────────────────

function ImageryTimelineSVG() {
  const ticks = 8
  const x1 = 50
  const x2 = 650
  const y = 50

  const thumbnailFills = ['#1a2a1a', '#243224', '#1a2a1a', '#2a3a2a', '#1e2e1e', '#243224', '#1a2a1a', '#22c55e30']

  return (
    <svg viewBox="0 0 700 100" className="w-full" aria-label="Imagery timeline">
      {/* Timeline baseline */}
      <line x1={x1} y1={y} x2={x2} y2={y} stroke="#1e3a1e" strokeWidth="2" />

      <text x={x1} y={y + 30} textAnchor="middle" fill="#64748b" fontSize="10">2019</text>
      <text x={x2} y={y + 30} textAnchor="middle" fill="#64748b" fontSize="10">2024</text>

      {Array.from({ length: ticks }, (_, i) => {
        const tx = x1 + (i / (ticks - 1)) * (x2 - x1)
        const isLatest = i === ticks - 1
        return (
          <g key={i}>
            {/* Thumbnail square */}
            <rect
              x={tx - 14} y={y - 38}
              width="28" height="20"
              rx="3"
              fill={thumbnailFills[i]}
              stroke={isLatest ? '#22c55e' : '#1e3a1e'}
              strokeWidth={isLatest ? 1.5 : 1}
            />
            {/* Tick mark */}
            <line x1={tx} y1={y - 18} x2={tx} y2={y + 6}
              stroke={isLatest ? '#22c55e' : '#374151'}
              strokeWidth={isLatest ? 2 : 1}
            />
            {/* Glow on latest */}
            {isLatest && (
              <circle cx={tx} cy={y} r="8"
                fill="none" stroke="#22c55e" strokeWidth="1.5" opacity="0.5" />
            )}
            <circle cx={tx} cy={y} r={isLatest ? 5 : 3}
              fill={isLatest ? '#22c55e' : '#374151'}
            />
            {/* Scrubber on latest */}
            {isLatest && (
              <>
                <polygon
                  points={`${tx - 7},${y + 10} ${tx + 7},${y + 10} ${tx},${y + 20}`}
                  fill="#22c55e"
                />
                <line x1={tx} y1={y + 20} x2={tx} y2={y + 35}
                  stroke="#22c55e" strokeWidth="1.5" strokeDasharray="3 2" />
              </>
            )}
          </g>
        )
      })}
    </svg>
  )
}

// ── SVG: Maritime Track ──────────────────────────────────────────────────────

function MaritimeTrackSVG() {
  // Waypoints for vessel track
  const waypoints = [
    [60,  200],
    [120, 185],
    [180, 165],
    [240, 155],
    [300, 145],
    [360, 138],
    [410, 125],
    [460, 118],
    [510, 108],
    [556, 98],
  ]
  const trackPoints = waypoints.map(([x, y]) => `${x},${y}`).join(' ')
  const last = waypoints[waypoints.length - 1]

  // Simple ship heading direction (approx angle from last two points)
  const prev = waypoints[waypoints.length - 2]
  const dx = last[0] - prev[0]
  const dy = last[1] - prev[1]
  const angle = Math.atan2(dy, dx) * (180 / Math.PI)

  return (
    <svg viewBox="0 0 640 260" className="w-full" aria-label="Maritime vessel track">
      {/* Ocean background */}
      <rect x="0" y="0" width="640" height="260" rx="12" fill="#060d0d" />

      {/* Nautical grid lines */}
      {Array.from({ length: 7 }, (_, i) => (
        <line key={`h${i}`} x1="0" y1={i * 40} x2="640" y2={i * 40}
          stroke="#0d1f1f" strokeWidth="1" />
      ))}
      {Array.from({ length: 9 }, (_, i) => (
        <line key={`v${i}`} x1={i * 80} y1="0" x2={i * 80} y2="260"
          stroke="#0d1f1f" strokeWidth="1" />
      ))}

      {/* Track polyline */}
      <polyline
        points={trackPoints}
        fill="none"
        stroke="#06b6d4"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
        opacity="0.8"
      />

      {/* Intermediate waypoints */}
      {waypoints.slice(0, -1).map(([x, y], i) => (
        <circle key={i} cx={x} cy={y} r="4"
          fill="#060d0d" stroke="#06b6d4" strokeWidth="1.5" opacity="0.6" />
      ))}

      {/* Latest position — larger with glow */}
      <circle cx={last[0]} cy={last[1]} r="18"
        fill="none" stroke="#22c55e" strokeWidth="1" opacity="0.3" />
      <circle cx={last[0]} cy={last[1]} r="12"
        fill="#0d1117" stroke="#22c55e" strokeWidth="2" />
      {/* Ship triangle pointing in direction of travel */}
      <g transform={`translate(${last[0]},${last[1]}) rotate(${angle})`}>
        <polygon
          points="0,-7 5,5 -5,5"
          fill="#22c55e"
        />
      </g>

      {/* Vessel info card */}
      <rect x="20" y="14" width="230" height="88" rx="8"
        fill="#0d1117" stroke="#1e3a1e" strokeWidth="1.5" />
      <text x="36" y="35" fill="#22c55e" fontSize="11" fontWeight="800">MV EXAMPLE</text>
      <text x="36" y="51" fill="#94a3b8" fontSize="9">MMSI: 123456789</text>
      <text x="36" y="63" fill="#94a3b8" fontSize="9">Speed: 12.4 kts   Heading: 048°</text>
      <text x="36" y="75" fill="#94a3b8" fontSize="9">Flag: SG   Type: Cargo</text>
      <text x="36" y="90" fill="#64748b" fontSize="8">Last AIS ping: 00:00:14 ago</text>

      {/* Risk badge */}
      <rect x="160" y="16" width="72" height="20" rx="5"
        fill="#22c55e18" stroke="#22c55e40" strokeWidth="1" />
      <text x="196" y="30" textAnchor="middle" fill="#22c55e" fontSize="8" fontWeight="700">LOW RISK</text>

      {/* Scale bar */}
      <line x1="490" y1="240" x2="590" y2="240" stroke="#1e3a1e" strokeWidth="1.5" />
      <line x1="490" y1="236" x2="490" y2="244" stroke="#1e3a1e" strokeWidth="1.5" />
      <line x1="590" y1="236" x2="590" y2="244" stroke="#1e3a1e" strokeWidth="1.5" />
      <text x="540" y="234" textAnchor="middle" fill="#374151" fontSize="8">50 nm</text>
    </svg>
  )
}

// ── SVG: MMSI Lookup Flow ────────────────────────────────────────────────────

function MMSILookupSVG() {
  const stages = [
    { label: 'MMSI',       sub: '9-digit ID',          color: '#3b82f6', x: 50  },
    { label: 'AIS Stream', sub: 'real-time position',   color: '#06b6d4', x: 180 },
    { label: 'Owner DB',   sub: 'flag + operator',      color: '#22c55e', x: 310 },
    { label: 'Port Hist.', sub: 'last 12 port calls',   color: '#f59e0b', x: 440 },
    { label: 'Risk Flag',  sub: 'sanctions + dark AIS', color: '#ef4444', x: 570 },
  ]
  return (
    <svg viewBox="0 0 660 120" className="w-full" aria-label="MMSI lookup flow">
      <line x1="50" y1="55" x2="620" y2="55"
        stroke="#1e3a1e" strokeWidth="1.5" strokeDasharray="6 4" />
      {stages.map((s, i) => (
        <g key={s.label}>
          {i > 0 && (
            <polygon
              points={`${s.x - 18},51 ${s.x - 6},55 ${s.x - 18},59`}
              fill="#374151"
            />
          )}
          <circle cx={s.x} cy="55" r="26" fill="#0d1117" stroke={s.color} strokeWidth="1.5" />
          <circle cx={s.x} cy="55" r="20" fill={`${s.color}18`} />
          <text x={s.x} y="59" textAnchor="middle" fill={s.color} fontSize="9" fontWeight="800">
            {String(i + 1)}
          </text>
          <text x={s.x} y="98" textAnchor="middle" fill="#e2e8f0" fontSize="10" fontWeight="600">{s.label}</text>
          <text x={s.x} y="112" textAnchor="middle" fill="#64748b" fontSize="9">{s.sub}</text>
        </g>
      ))}
    </svg>
  )
}

// ── SVG: Change Detection ────────────────────────────────────────────────────

function ChangeDetectionSVG() {
  return (
    <svg viewBox="0 0 540 160" className="w-full" aria-label="Change detection before/after">
      {/* Before image box */}
      <rect x="20" y="20" width="180" height="120" rx="8"
        fill="#111a0f" stroke="#1e3a1e" strokeWidth="1.5" />
      <text x="110" y="14" textAnchor="middle" fill="#64748b" fontSize="10" fontWeight="600">BEFORE — 2022</text>
      {/* Simulated imagery blocks */}
      <rect x="30" y="30" width="160" height="100" rx="4" fill="#1a2a14" />
      <rect x="40" y="40" width="60" height="40" rx="3" fill="#243a1c" />
      <rect x="110" y="50" width="70" height="50" rx="3" fill="#1e3216" />
      <rect x="50" y="90" width="90" height="32" rx="3" fill="#1c2e14" />

      {/* Arrow */}
      <text x="270" y="88" textAnchor="middle" fill="#374151" fontSize="28" fontWeight="300">→</text>

      {/* After image box */}
      <rect x="340" y="20" width="180" height="120" rx="8"
        fill="#111a0f" stroke="#1e3a1e" strokeWidth="1.5" />
      <text x="430" y="14" textAnchor="middle" fill="#64748b" fontSize="10" fontWeight="600">AFTER — 2024</text>
      {/* Same imagery but with changed area highlighted */}
      <rect x="350" y="30" width="160" height="100" rx="4" fill="#1a2a14" />
      <rect x="360" y="40" width="60" height="40" rx="3" fill="#243a1c" />
      <rect x="430" y="50" width="70" height="50" rx="3" fill="#1e3216" />
      <rect x="370" y="90" width="90" height="32" rx="3" fill="#1c2e14" />
      {/* Changed region overlay */}
      <rect x="360" y="40" width="60" height="40" rx="3"
        fill="rgba(239,68,68,0.18)" stroke="#ef4444" strokeWidth="1.5" />
      <text x="390" y="65" textAnchor="middle" fill="#ef4444" fontSize="8" fontWeight="700">+18%</text>
      <rect x="430" y="60" width="45" height="30" rx="3"
        fill="rgba(239,68,68,0.14)" stroke="#ef4444" strokeWidth="1.5" />
      <text x="452" y="79" textAnchor="middle" fill="#ef4444" fontSize="8" fontWeight="700">+7%</text>

      {/* Diff legend */}
      <rect x="340" y="148" width="12" height="8" rx="2" fill="rgba(239,68,68,0.4)" stroke="#ef4444" strokeWidth="1" />
      <text x="357" y="156" fill="#64748b" fontSize="9">Changed region</text>
    </svg>
  )
}

// ── Constants ────────────────────────────────────────────────────────────────

const stats = [
  { value: '40+',    label: 'Years imagery archive' },
  { value: '120K+',  label: 'Vessels tracked live' },
  { value: '200m',   label: 'Resolution' },
  { value: 'Global', label: 'Coverage' },
]

const vesselCapabilities = [
  'MMSI to owner lookup — registered operator, IMO, flag state',
  'Last 12 port calls with arrival/departure timestamps',
  'Cargo declarations cross-referenced against trade databases',
  'Dark AIS detection — gap analysis on AIS transmission history',
  'Sanctions screening against UN, OFAC, and EU vessel lists',
  'Risk flag composite score with evidence trail',
]

const fadeUp = {
  hidden: { opacity: 0, y: 28 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.55 } },
}

// ── Page ─────────────────────────────────────────────────────────────────────

export default function GEOINTProduct() {
  return (
    <div className="min-h-screen bg-[#0a0f0a] text-white font-sans">
      <SEOHead
        title="GEOINT — Satellite Imagery Intelligence & Geospatial Analysis | Sycek OSINT"
        description="Historical satellite imagery analysis with AOI monitoring, change detection, spectral analysis, and SAR. Monitor locations over time, detect infrastructure changes, and build target packages with geospatial intelligence for investigators and security teams."
        keywords="GEOINT, geospatial intelligence, satellite imagery analysis, AOI monitoring, area of interest, change detection, satellite OSINT, Wayback imagery, spectral analysis, SAR analysis, target package, geospatial analysis, location intelligence, satellite intelligence, remote sensing OSINT"
        canonical="/geoint"
        jsonLd={{
          "@type": "Product",
          "name": "GEOINT — Geospatial Intelligence",
          "description": "Historical satellite imagery, AOI monitoring, change detection, and geospatial analysis for investigators and security professionals.",
          "brand": { "@type": "Brand", "name": "Sycek OSINT" },
          "url": "https://sycek.io/geoint",
          "category": "Security Software"
        }}
      />
      <ProductPageNav activePage="GEOINT" />

      {/* ── Hero ── */}
      <section className="relative overflow-hidden py-28 px-6">
        <div className="pointer-events-none absolute inset-0">
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[700px] h-[400px] bg-green-500/[0.05] rounded-full blur-3xl" />
        </div>
        <div className="container mx-auto max-w-4xl text-center relative z-10">
          <motion.div initial="hidden" animate="visible" variants={fadeUp}>
            <Badge className="mb-6 bg-green-500/10 text-green-400 border-green-500/20 text-xs uppercase tracking-widest px-4 py-1.5">
              Geospatial Intelligence
            </Badge>
            <h1 className="text-5xl md:text-6xl font-black leading-tight tracking-tight mb-6">
              Eyes on{' '}
              <span className="text-green-400">Every Coordinate</span>
            </h1>
            <p className="text-lg text-slate-400 max-w-2xl mx-auto mb-10 leading-relaxed">
              40+ years of satellite imagery, real-time AIS vessel tracking, and automated
              area-of-interest change detection — unified in a single geospatial intelligence
              workspace.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/app/geoint-wayback">
                <Button size="lg" className="bg-green-500 hover:bg-green-600 text-black font-bold px-8 shadow-lg shadow-green-500/30">
                  Open GEOINT
                </Button>
              </Link>
              <Link to="/app/geoint-maritime">
                <Button size="lg" variant="outline" className="border-white/10 hover:border-green-500/40 hover:bg-green-500/[0.06] text-slate-300">
                  Maritime Tracking
                </Button>
              </Link>
            </div>
          </motion.div>
        </div>
      </section>

      {/* ── Stats ── */}
      <section className="border-y border-white/[0.06] py-14 px-6">
        <div className="container mx-auto max-w-4xl">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {stats.map((s) => (
              <motion.div
                key={s.label}
                initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp}
                className="text-center"
              >
                <div className="text-4xl font-black text-green-400 mb-1">{s.value}</div>
                <div className="text-sm text-slate-500">{s.label}</div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ── AOI Satellite SVG ── */}
      <section className="py-20 px-6">
        <div className="container mx-auto max-w-5xl">
          <motion.div
            initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp}
            className="text-center mb-12"
          >
            <h2 className="text-3xl font-bold mb-3">Area of Interest Monitoring</h2>
            <p className="text-slate-400 max-w-xl mx-auto text-sm">
              Draw a polygon over any location. Sycek auto-monitors for new satellite
              imagery and fires an alert whenever change is detected inside your AOI.
            </p>
          </motion.div>
          <motion.div
            initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp}
            className="rounded-2xl border border-white/[0.07] bg-[#0d1117] p-6"
          >
            <AOISatelliteSVG />
          </motion.div>
          <motion.div
            initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp}
            className="mt-8 grid grid-cols-1 sm:grid-cols-3 gap-4"
          >
            {[
              { label: 'Wayback Imagery', desc: 'Scrubs Wayback Machine + Google Earth Engine tiles for any lat/long' },
              { label: 'Auto-Alert', desc: 'Push notification when new imagery or change is detected in your AOI' },
              { label: 'Change Quantified', desc: 'Pixel-level diff gives a numeric change percentage, not just a visual overlay' },
            ].map((f) => (
              <div key={f.label} className="rounded-xl border border-white/[0.07] bg-[#0d1117] p-5">
                <div className="flex items-center gap-2 mb-2">
                  <CheckCircle className="w-4 h-4 text-green-400 flex-shrink-0" />
                  <span className="text-sm font-semibold text-white">{f.label}</span>
                </div>
                <p className="text-xs text-slate-500">{f.desc}</p>
              </div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* ── Imagery Timeline SVG ── */}
      <section className="py-20 px-6 bg-[#0c110c]">
        <div className="container mx-auto max-w-5xl">
          <motion.div
            initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp}
            className="text-center mb-12"
          >
            <h2 className="text-3xl font-bold mb-3">Historical Imagery Timeline</h2>
            <p className="text-slate-400 max-w-xl mx-auto text-sm">
              Scrub through four decades of satellite passes for any coordinate.
              The timeline highlights available acquisitions and the latest high-res capture.
            </p>
          </motion.div>
          <motion.div
            initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp}
            className="rounded-2xl border border-white/[0.07] bg-[#0d1117] p-6"
          >
            <ImageryTimelineSVG />
          </motion.div>
        </div>
      </section>

      {/* ── Maritime Track SVG ── */}
      <section className="py-20 px-6">
        <div className="container mx-auto max-w-5xl">
          <motion.div
            initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp}
            className="text-center mb-12"
          >
            <h2 className="text-3xl font-bold mb-3">Real-Time AIS Vessel Tracking</h2>
            <p className="text-slate-400 max-w-xl mx-auto text-sm">
              Live feed from AISStream WebSocket. Filter by vessel type, flag, MMSI range,
              or geographic bounding box. Full track history for every vessel.
            </p>
          </motion.div>
          <motion.div
            initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp}
            className="rounded-2xl border border-white/[0.07] overflow-hidden"
          >
            <MaritimeTrackSVG />
          </motion.div>
          <motion.div
            initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp}
            className="mt-8 grid grid-cols-1 sm:grid-cols-2 gap-4"
          >
            {[
              { icon: Navigation,    label: 'Live position stream',    desc: 'Sub-minute position updates via AISStream WebSocket' },
              { icon: Eye,           label: 'Track history replay',    desc: 'Full course replay for any vessel — configurable lookback' },
              { icon: AlertTriangle, label: 'Dark AIS detection',      desc: 'Alert when a vessel goes silent in a high-risk zone' },
              { icon: Anchor,        label: 'Port call history',       desc: 'Last 12 port arrivals with timestamps and berth details' },
            ].map((f) => (
              <div key={f.label} className="rounded-xl border border-white/[0.07] bg-[#0d1117] p-5 flex items-start gap-4">
                <f.icon className="w-5 h-5 text-green-400 mt-0.5 flex-shrink-0" />
                <div>
                  <div className="text-sm font-semibold text-white mb-1">{f.label}</div>
                  <div className="text-xs text-slate-500">{f.desc}</div>
                </div>
              </div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* ── Vessel Intelligence ── */}
      <section className="py-20 px-6 bg-[#0c110c]">
        <div className="container mx-auto max-w-5xl">
          <motion.div
            initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp}
            className="text-center mb-12"
          >
            <h2 className="text-3xl font-bold mb-3">Vessel Intelligence</h2>
            <p className="text-slate-400 max-w-xl mx-auto text-sm">
              One MMSI number unlocks the full intelligence picture — from registered owner
              to sanctions exposure.
            </p>
          </motion.div>
          <div className="grid md:grid-cols-2 gap-10 items-center">
            <motion.div
              initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp}
              className="rounded-2xl border border-white/[0.07] bg-[#0d1117] p-6"
            >
              <MMSILookupSVG />
            </motion.div>
            <motion.div
              initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp}
            >
              <h3 className="text-lg font-bold text-white mb-5">What the lookup delivers</h3>
              <ul className="space-y-3">
                {vesselCapabilities.map((cap) => (
                  <li key={cap} className="flex items-start gap-3 text-sm text-slate-400">
                    <CheckCircle className="w-4 h-4 text-green-400 mt-0.5 flex-shrink-0" />
                    {cap}
                  </li>
                ))}
              </ul>
            </motion.div>
          </div>
        </div>
      </section>

      {/* ── Change Detection ── */}
      <section className="py-20 px-6">
        <div className="container mx-auto max-w-5xl">
          <motion.div
            initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp}
            className="text-center mb-12"
          >
            <h2 className="text-3xl font-bold mb-3">Before / After Change Detection</h2>
            <p className="text-slate-400 max-w-xl mx-auto text-sm">
              Side-by-side comparison of any two image dates, with highlighted change regions
              and a numeric change percentage per detected cluster.
            </p>
          </motion.div>
          <motion.div
            initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp}
            className="rounded-2xl border border-white/[0.07] bg-[#0d1117] p-6"
          >
            <ChangeDetectionSVG />
          </motion.div>
          <motion.div
            initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp}
            className="mt-8 grid grid-cols-1 sm:grid-cols-3 gap-4"
          >
            {[
              { icon: Eye,        label: 'Pixel-level diff',       desc: 'Change detection at 200 m resolution, no manual inspection needed' },
              { icon: MapPin,     label: 'Change cluster export',  desc: 'Export changed regions as GeoJSON polygons for downstream tools' },
              { icon: Satellite,  label: 'Multi-source fusion',    desc: 'Combines Wayback + Sentinel-2 + commercial imagery when available' },
            ].map((f) => (
              <div key={f.label} className="rounded-xl border border-white/[0.07] bg-[#0d1117] p-5">
                <div className="flex items-center gap-2 mb-2">
                  <f.icon className="w-4 h-4 text-green-400 flex-shrink-0" />
                  <span className="text-sm font-semibold text-white">{f.label}</span>
                </div>
                <p className="text-xs text-slate-500">{f.desc}</p>
              </div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* ── CTA ── */}
      <section className="py-24 px-6">
        <div className="container mx-auto max-w-3xl text-center">
          <motion.div
            initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp}
          >
            <div className="rounded-2xl border border-green-500/20 bg-green-500/[0.04] px-10 py-14">
              <h2 className="text-4xl font-black mb-4">Start Geospatial Investigation</h2>
              <p className="text-slate-400 mb-8 max-w-md mx-auto text-sm">
                Drop a pin, draw an AOI, or enter an MMSI. Full geospatial intelligence
                workspace ready in seconds.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Link to="/app/geoint-wayback">
                  <Button size="lg" className="bg-green-500 hover:bg-green-600 text-black font-bold px-10 shadow-lg shadow-green-500/30">
                    Start Geospatial Investigation
                  </Button>
                </Link>
                <Link to="/app/geoint-maritime">
                  <Button size="lg" variant="outline" className="border-white/10 hover:border-green-500/40 text-slate-300">
                    Maritime Tracking
                  </Button>
                </Link>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      <ProductPageFooter />
    </div>
  )
}
